import { useCallback, useEffect, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import { filterTerms, newSuggestions, parseTermsInput } from '../../lib/glossaryView';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';

interface Suggestion {
  chunks: number;
  candidates: number;
  chosen: string[];
}

/**
 * Knowledge › Glossary (A2 step C3): the Latin terms this bot's answers never transliterate. A protected term blocks that
 * word for good, and no rule tells `POS` (пос, a Mongolian word) from `EEG` (must stay Latin) — so terms are curated:
 * add by hand, remove mistakes, and "suggest" asks an LLM over the bot's chunks in a dry run; only ticked ones are added.
 */
export function GlossaryPanel({ system, botId }: { system: string; botId: string }) {
  const [terms, setTerms] = useState<string[] | null>(null);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState('');
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const k = useCallback(
    <T,>(method: 'GET' | 'POST' | 'DELETE', rest: string, body?: unknown) => hubService.knowledge<T>(system, botId, method, rest, body),
    [system, botId],
  );

  const load = useCallback(async () => {
    try {
      const r = await k<{ terms: string[] }>('GET', 'glossary');
      setTerms(Array.isArray(r?.terms) ? [...r.terms].sort((a, b) => a.localeCompare(b)) : []);
    } catch (e) {
      setTerms([]);
      setError(errorText(e));
    }
  }, [k]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(list: string[], label: string) {
    if (!list.length) return;
    setBusy(label);
    setError(null);
    try {
      const r = await k<{ added: string[]; total: number | null }>('POST', 'glossary', { terms: list });
      setNotice(`${r.added.length} added${r.added.length < list.length ? ` (${list.length - r.added.length} already there)` : ''}.`);
      setAdding('');
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!selected.length) return;
    if (!window.confirm(`Remove ${selected.length} term(s)? The bot may then transliterate them (e.g. QPay → Кюпэй).`)) return;
    setBusy('remove');
    setError(null);
    try {
      await k('DELETE', 'glossary', { terms: selected });
      setNotice(`${selected.length} removed.`);
      setSelected([]);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function suggest() {
    setBusy('suggest');
    setError(null);
    setSuggestion(null);
    try {
      const r = await k<Suggestion>('POST', 'glossary/suggest');
      setSuggestion(r);
      setPicked(newSuggestions(r.chosen, terms ?? []));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (terms === null) return <p className="text-sm text-gray-500">Loading…</p>;
  const shown = filterTerms(terms, filter);
  const fresh = suggestion ? newSuggestions(suggestion.chosen, terms) : [];

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-5">
      <div>
        <h2 className="font-medium text-gray-900">Latin glossary</h2>
        <p className="text-sm text-gray-600">
          Words this bot always writes in Latin letters (brands, codes, medical terms). A wrong entry stops a real Mongolian word from being
          transliterated, so add carefully and remove mistakes.
        </p>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {notice && <div className="text-sm text-green-800 bg-green-50 border border-green-100 rounded px-3 py-2">{notice}</div>}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-medium text-gray-800 mr-auto">{terms.length} term(s)</h3>
          <input className={input} placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <button type="button" className="text-sm text-red-600 hover:underline disabled:opacity-40" disabled={!selected.length || busy !== null} onClick={() => void remove()}>
            Remove {selected.length || ''} selected
          </button>
        </div>
        {terms.length === 0 ? (
          <p className="text-sm text-gray-500">No terms yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-64 overflow-auto border rounded p-2">
            {shown.map((t) => {
              const on = selected.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSelected((s) => (on ? s.filter((x) => x !== t) : [...s, t]))}
                  className={`text-xs px-2 py-0.5 rounded border ${on ? 'bg-red-50 border-red-300 text-red-800' : 'bg-gray-50 border-gray-200 text-gray-800'}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-800">Add</h3>
        <textarea className={`w-full ${input}`} rows={2} placeholder="QPay, HbA1c, POS-terminal (commas or new lines)" value={adding} onChange={(e) => setAdding(e.target.value)} />
        <button
          type="button"
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
          disabled={!parseTermsInput(adding).length || busy !== null}
          onClick={() => void add(parseTermsInput(adding), 'add')}
        >
          Add {parseTermsInput(adding).length || ''} term(s)
        </button>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-800">Suggest from the bot’s chunks</h3>
        <p className="text-xs text-gray-500">An LLM reads this bot’s chunks and proposes terms — nothing is added until you tick and add them. Takes up to a few minutes.</p>
        <button type="button" className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded disabled:opacity-50" disabled={busy !== null} onClick={() => void suggest()}>
          {busy === 'suggest' ? 'Reading the chunks…' : 'Suggest terms'}
        </button>
        {suggestion && (
          <div className="border rounded p-2 space-y-2">
            <p className="text-sm">
              {suggestion.chunks} chunk(s) read · {suggestion.candidates} candidate(s) · {suggestion.chosen.length} suggested
              {suggestion.chosen.length ? ` · ${fresh.length} new` : ''}.
            </p>
            {fresh.length > 0 && (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {fresh.map((t) => {
                    const on = picked.includes(t);
                    return (
                      <label key={t} className={`text-xs px-2 py-0.5 rounded border cursor-pointer ${on ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 text-gray-500'}`}>
                        <input
                          type="checkbox"
                          className="mr-1 align-middle"
                          checked={on}
                          onChange={() => setPicked((p) => (on ? p.filter((x) => x !== t) : [...p, t]))}
                        />
                        {t}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                  disabled={!picked.length || busy !== null}
                  onClick={() => void add(picked, 'suggest-add').then(() => setSuggestion(null))}
                >
                  Add {picked.length} ticked
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
